<script lang="ts">
	import { onMount } from 'svelte'
	import bv from 'shared/object/body_validator'
	import 'leaflet'
	import 'leaflet/dist/leaflet.css'

	const dealer_validator = bv.array(bv.object({
		name: bv.string,
		lat: bv.number,
		lng: bv.number,
		href: bv.optional(bv.string),
		address: bv.optional(bv.string),
		ecommerce: bv.optional(bv.boolean),
	}))

	type Dealer = {
		name: string
		lat: number
		lng: number
		href: string | undefined
		address: string | undefined
		ecommerce: boolean | undefined
	}

	export let dealers: Dealer[]
	export let zoom: number = 4
	export let center_lat: number = 39.8283 // Default to center of US
	export let center_lng: number = -98.5795

	let map_element: HTMLElement
	let map: any
	let markers: Record<string, any> = {}
	let errors: string[] = []
	let sorted_dealers: Dealer[] = []
	let selected_dealer_id: string | null = null

	// Handle JSON string input
	if (typeof dealers === `string`) {
		try {
			dealers = JSON.parse(dealers)
		} catch (e: any) {
			console.error(e)
			errors = [ e.toString() ]
		}
	}

	if (!dealer_validator.is_valid(dealers)) {
		errors.push(...dealer_validator.get_messages(dealers, `dealers`))
	}

	$: if (dealer_validator.is_valid(dealers)) {
		sorted_dealers = [ ...dealers ].sort((a, b) => a.name.localeCompare(b.name))
	}

	function get_dealer_id(dealer: Dealer): string {
		return `${ dealer.lat }-${ dealer.lng }-${ dealer.name }`
	}

	function select_dealer(dealer: Dealer) {
		const id = get_dealer_id(dealer)
		selected_dealer_id = id

		map.setView([ dealer.lat, dealer.lng ], map.getZoom())
		markers[id].openPopup()

		const element = document.getElementById(`dealer-${ id }`)
		if (element) {
			element.scrollIntoView({ behavior: `smooth`, block: `nearest` })
		}
	}

	onMount(async() => {
		if (!dealer_validator.is_valid(dealers)) { return }

		const L = window.L as any

		const icon_params = {
			className: `dealer-marker-container`,
			html: `<div class="dealer-marker"></div>`,
			// 24 is bigger than 10 so there's a bigger touch target
			iconSize: [ 24, 24 ],
			iconAnchor: [ 10, 10 ],
			popupAnchor: [ 3, -5 ],
		}

		const retail_icon = L.divIcon(icon_params)
		const ecommerce_icon = L.divIcon({
			...icon_params,
			className: `dealer-marker-container ecommerce`,
		})

		map = L.map(map_element, {
			attributionControl: false,
		}).setView([ center_lat, center_lng ], zoom)

		L.control.attribution({
			prefix: false,
		}).addTo(map)

		// Add dark theme tile layer
		L.tileLayer(`https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png`, {
			attribution: `&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors`,
		}).addTo(map)

		dealers.forEach(dealer => {
			const id = get_dealer_id(dealer)
			const popup_content = `
				${ dealer.href ? `<a href="${ dealer.href }" target="_blank" class="${ dealer.ecommerce ? `ecommerce` : `` }">${ dealer.name }<div class="url">${ dealer.href }</div></a>` : `<strong>${ dealer.name }</strong>` }
				${ dealer.address || `` }`

			const marker = L.marker([ dealer.lat, dealer.lng ], { icon: dealer.ecommerce ? ecommerce_icon : retail_icon })
				.bindPopup(popup_content)
				.addTo(map)

			markers[id] = marker

			marker.on(`click`, () => {
				select_dealer(dealer)
			})
		})
	})

	$: brick_n_mortar_dealers = sorted_dealers.filter(d => !d.ecommerce)
	$: ecommerce_dealers = sorted_dealers.filter(d => d.ecommerce)
</script>

{#each errors as error}
	<p class="error">{error}</p>
{/each}

{#if dealer_validator.is_valid(dealers)}
	<div class="container">
		<div bind:this={map_element} class="map"></div>
		<div class="dealer-list">
			{#each brick_n_mortar_dealers as dealer}
				{@const id = get_dealer_id(dealer)}
				<button
					id="dealer-{id}"
					class="dealer-item"
					class:selected={selected_dealer_id === id}
					on:click={() => select_dealer(dealer)}
				>
					{#if dealer.href}
						<a href={dealer.href} target="_blank">{ dealer.name }</a>
					{:else}
						<strong>{ dealer.name }</strong>
					{/if}
					{#if dealer.address}
						<span>{ dealer.address }</span>
					{/if}
				</button>
			{/each}

			{#if ecommerce_dealers.length}
				<h2>Online Dealers</h2>
				{#each ecommerce_dealers as dealer}
					{@const id = get_dealer_id(dealer)}
					<button
						id="dealer-{id}"
						class="dealer-item ecommerce"
						class:selected={selected_dealer_id === id}
						on:click={() => select_dealer(dealer)}
					>
						{#if dealer.href}
							<a class=title href={dealer.href} target="_blank">{ dealer.name }</a>
						{:else}
							<strong>{ dealer.name }</strong>
						{/if}
						{#if dealer.href}
							<a class=url href={dealer.href} target="_blank">{ dealer.href }</a>
						{/if}
					</button>
				{/each}
			{/if}
		</div>
	</div>
{/if}

<style>
	.container {
		display: flex;
		height: 600px;
		--highlight-color: #ceff51;
	}

	.ecommerce, :global(.map .ecommerce) {
		--highlight-color: #51ceff;
	}

	.dealer-list {
		width: 300px;
		overflow-y: auto;
		background: #1a1a1a;
		border-radius: 4px;
	}

	.dealer-list h2 {
		font-size: 1rem;
		color: #fff;
		padding: 2rem 0 0.5rem 0;
		margin: 0 1rem;
		border-bottom: 1px solid #fff;
		font-weight: 500;
	}

	a {
		color: var(--highlight-color);
	}

	.dealer-item {
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: 1rem;
		gap: 0.5rem;
		text-align: left;
		background: none;
		border: none;
		border-bottom: 1px solid #2a2a2a;
		cursor: pointer;
		text-transform: none;
		color: #bbb;
		font-weight: 400;
		letter-spacing: 0;
	}

	.dealer-item strong, .dealer-item a.title {
		font-weight: 500;
		letter-spacing: 1px;
	}

	.dealer-item strong {
		color: white;
	}

	.dealer-item .url {
		white-space: nowrap;
		text-overflow: ellipsis;
		overflow: hidden;
		color: #bbb;
		font-weight: 400;
	}

	.dealer-item:hover,
	.dealer-item.selected {
		background: black;
	}

	@media (max-width: 800px) {
		.container {
			flex-direction: column;
			height: 800px;
		}

		.dealer-list {
			width: 100%;
			height: 400px;
		}
	}

	.error {
		color: red;
	}

	.map {
		flex: 1;
		z-index: 1;
	}

	:global(.leaflet-control-zoom a) {
		background-color: #2a2a2a !important;
		color: #fff !important;
		border-color: #444 !important;
	}

	:global(.leaflet-control-zoom a:hover) {
		background-color: #3a3a3a !important;
	}

	:global(.leaflet-control-attribution) {
		background-color: rgba(42, 42, 42, 0.8) !important;
		color: #888 !important;
	}

	:global(.leaflet-control-attribution a) {
		color: #fff !important;
	}

	:global(.leaflet-popup-content-wrapper) {
		background: #2a2a2a;
		border-radius: 4px;
		box-shadow: none;
		border: 1px solid #444;
	}

	:global(.leaflet-popup-content) {
		color: #bbb;
		font-family: "Protipo Wide", "Helvetica Neue", Helvetica, Arial, sans-serif;
		font-size: 14px;
		display: flex;
		flex-direction: column;
		gap: 0.5rem;
	}

	:global(.leaflet-popup-content-wrapper strong, .leaflet-popup-content-wrapper a) {
		letter-spacing: 1px;
		font-weight: 500;
	}

	:global(.leaflet-popup-content-wrapper strong) {
		color: white
	}

	:global(.leaflet-popup-content-wrapper a) {
		color: var(--highlight-color);
	}

	:global(.leaflet-popup-content-wrapper .url) {
		color: #999;
		font-size: 10px;
		letter-spacing: 0;
	}

	:global(.leaflet-popup-tip) {
		background: #2a2a2a;
		border: 1px solid #444;
	}

	:global(.dealer-marker-container) {
		display: flex;
		align-items: center;
		justify-content: center;
	}

	:global(.dealer-marker) {
		width: 10px;
		height: 10px;
		background: var(--highlight-color);
		clip-path: polygon(0 0, 100% 0, 100% 70%, 70% 100%, 0 100%);
	}

	:global(.dealer-marker-container:hover .dealer-marker) {
		background: white;
	}
</style>

